<template>
  <div class="l-main">
    <div class="l-main__header">
      <ul class="c-breadcrumbs">
        <li class="c-breadcrumbs__item">
          <a
            class="c-breadcrumbs__link"
            @click="goToHome"
          > 
            <md-icon>home</md-icon>
            <span>Home</span>
          </a>
        </li>
        <li class="c-breadcrumbs__item--separator">
          /
        </li>
        <li class="c-breadcrumbs__item is-current">
          <a class="c-breadcrumbs__link c-breadcrumbs__home">Terminal Payments</a>
        </li>
      </ul>
    </div>
    <div class="l-main__ctn u-box">
      <div
        v-if="isFetchingCurrencies"
        class="c-payments__loading"
      >
        <span class="ui-spinner" />
      </div>

      <template v-else>
        <div class="c-page-header">
          <div>
            <h2 class="c-page-header__title">
              Terminal payments
            </h2>
            <p class="c-page-header__subtitle">
              Money transfers made through betshop terminals.
            </p>
          </div>
        </div>

        <!-- filter bar -->
        <div
          class="c-payments__filterbar"
        >
          <div class="c-date-range">
            <app-icon
              class="c-date-range__icon"
              name="calendar_month"
            />
            <label class="c-date-range__field">
              <span class="c-date-range__label">From</span>
              <input
                v-model="filters.fromTime"
                type="date"
                aria-label="From date"
                :max="maxFromDate"
                onkeydown="return false"
              >
            </label>
            <app-icon
              class="c-date-range__arrow"
              name="arrow_forward"
              :size="16"
            />
            <label class="c-date-range__field">
              <span class="c-date-range__label">To</span>
              <input
                v-model="filters.toTime"
                type="date"
                aria-label="To date"
                :max="today"
                :min="minToDate"
                onkeydown="return false"
              >
            </label>
          </div>

          <div
            class="c-payments__presets"
            role="group"
            aria-label="Date presets"
          >
            <button
              v-for="preset in datePresets"
              :key="preset.days"
              type="button"
              :class="['c-payments__preset', { 'is-active': isPresetActive(preset) }]"
              :aria-pressed="String(isPresetActive(preset))"
              @click="applyDatePreset(preset)"
            >
              {{ preset.label }}
            </button>
          </div>

          <div class="c-payments__status">
            <select
              v-model="filters.status"
              class="ui-select"
              aria-label="Status"
            >
              <option
                v-for="paymentStatus in paymentStatuses"
                :key="paymentStatus.key"
                :value="paymentStatus.key"
              >
                {{ paymentStatus.name }}
              </option>
            </select>
          </div>

          <div class="c-payments__actions">
            <button
              type="button"
              :class="['ui-btn ui-btn--secondary', { 'is-open': filtersAreVisible }]"
              :aria-expanded="String(filtersAreVisible)"
              aria-controls="payments-more-filters"
              @click="toggleFilters()"
            >
              <app-icon name="tune" />
              Filters
              <span
                v-if="pendingAdvancedCount"
                class="c-payments__count"
              >{{ pendingAdvancedCount }}</span>
            </button>
            <button
              type="button"
              class="ui-btn ui-btn--primary"
              @click="getDataPerPage(1)"
            >
              <app-icon name="search" />
              Apply
            </button>
          </div>
        </div>

        <!-- more filters -->
        <transition name="c-payments-panel">
          <div
            v-show="filtersAreVisible"
            id="payments-more-filters"
            class="c-payments__panel"
          >
            <div class="c-payments__grid">
              <label class="c-payments__field">
                <span>Min amount</span>
                <input
                  v-model="filters.minAmount"
                  v-check-digit
                  class="ui-field__input"
                  type="number"
                  placeholder="0.00"
                >
              </label>
              <label class="c-payments__field">
                <span>Max amount</span>
                <input
                  v-model="filters.maxAmount"
                  v-check-digit
                  class="ui-field__input"
                  type="number"
                  placeholder="Any"
                >
              </label>
              <label class="c-payments__field">
                <span>Currency</span>
                <select
                  v-model="filters.currency"
                  class="ui-select"
                >
                  <option
                    v-for="currency in currencies"
                    :key="currency.key"
                    :value="currency.key"
                  >
                    {{ currency.name || currency.key }}
                  </option>
                </select>
              </label>
              <label class="c-payments__field">
                <span>Terminal ID</span>
                <input
                  v-model="filters.terminalId"
                  v-check-digit
                  class="ui-field__input"
                  type="number"
                  placeholder="Any terminal"
                >
              </label>
              <label class="c-payments__field">
                <span>Sender ID</span>
                <input
                  v-model="filters.senderId"
                  v-check-digit
                  class="ui-field__input"
                  type="number"
                  placeholder="Any sender"
                >
              </label>
              <label class="c-payments__field">
                <span>Receiver ID</span>
                <input
                  v-model="filters.receiverId"
                  v-check-digit
                  class="ui-field__input"
                  type="number"
                  placeholder="Any receiver"
                >
              </label>
              <label class="c-payments__field">
                <span>New system payment ID</span>
                <input
                  v-model="filters.newSystemTransactionId"
                  v-check-digit
                  class="ui-field__input"
                  type="number"
                  placeholder="Any"
                >
              </label>
              <label class="c-payments__field">
                <span>Old system payment ID</span>
                <input
                  v-model="filters.oldSystemTransactionId"
                  v-check-digit
                  class="ui-field__input"
                  type="number"
                  placeholder="Any"
                >
              </label>
            </div>

            <div class="c-payments__panel-footer">
              <button
                type="button"
                class="ui-btn ui-btn--ghost"
                @click="toggleFilters()"
              >
                Close
              </button>
              <button
                type="button"
                class="ui-btn ui-btn--primary"
                @click="getDataPerPage(1)"
              >
                Apply filters
              </button>
            </div>
          </div>
        </transition>

        <!-- applied filters -->
        <div
          v-if="formattedFilters.length || appliedDateLabel"
          class="c-payments__applied"
        >
          <span class="c-payments__applied-label">Filtered by</span>
          <span
            v-if="appliedDateLabel"
            class="c-payments__tag"
          >
            {{ appliedDateLabel }}
            <button
              type="button"
              :aria-label="`Remove date filter`"
              @click="clearDateFilter()"
            >
              <app-icon
                name="close"
                :size="14"
              />
            </button>
          </span>
          <span
            v-for="filter in formattedFilters"
            :key="filter.key"
            class="c-payments__tag"
          >
            {{ filter.name }}: <strong>{{ filter.value }}</strong>
            <button
              type="button"
              :aria-label="`Remove ${filter.name} filter`"
              @click="clearFilter(filter.key)"
            >
              <app-icon
                name="close"
                :size="14"
              />
            </button>
          </span>
          <button
            type="button"
            class="c-payments__clear-all"
            @click="clearFilters()"
          >
            Clear all
          </button>
        </div>

        <!-- results -->
        <div class="c-payments__results">
          <div
            v-if="isFetchingPaymentsPage"
            class="c-payments__overlay"
          >
            <span class="ui-spinner" />
          </div>

          <table
            v-if="payments.length"
            class="c-payments-table"
          >
            <thead>
              <tr>
                <th>Time</th>
                <th>Sender</th>
                <th>Receiver</th>
                <th>Terminal</th>
                <th>Payment ID</th>
                <th>Status</th>
                <th class="is-numeric">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(payment, index) in payments"
                :key="`${payment.newSystemTransactionId}-${payment.oldSystemTransactionId}-${index}`"
              >
                <td>
                  <span class="c-payments-table__primary">{{ formatTime(payment.timeStamp) }}</span>
                  <span class="c-payments-table__secondary">{{ formatDate(payment.timeStamp) }}</span>
                </td>
                <td>
                  <span class="c-payments-table__primary">{{ payment.senderFullName || '—' }}</span>
                  <span class="c-payments-table__id">ID {{ payment.senderId }}</span>
                </td>
                <td>
                  <span class="c-payments-table__id c-payments-table__id--main">{{ payment.receiverId }}</span>
                </td>
                <td>
                  <span class="c-payments-table__id c-payments-table__id--main">{{ payment.terminalId }}</span>
                </td>
                <td>
                  <span class="c-payments-table__id c-payments-table__id--main">{{ payment.newSystemTransactionId || '—' }}</span>
                  <span class="c-payments-table__id">Old {{ payment.oldSystemTransactionId || '—' }}</span>
                </td>
                <td>
                  <span :class="['c-payments-table__status', `c-payments-table__status--${String(payment.status).toLowerCase()}`]">
                    <app-icon
                      :name="payment.status === 'SUCCESSFUL' ? 'check_circle' : 'cancel'"
                      :size="14"
                      filled
                    />
                    {{ formatStatus(payment.status) }}
                  </span>
                </td>
                <td class="is-numeric">
                  <span class="c-payments-table__amount">{{ formatAmount(payment.amount) }}</span>
                  <span class="c-payments-table__currency">{{ payment.currency }}</span>
                </td>
              </tr>
            </tbody>
          </table>

          <div
            v-else-if="!isFetchingPaymentsPage"
            class="c-table__empty"
          >
            <span class="c-table__empty-icon">
              <app-icon
                name="receipt_long"
                :size="24"
              />
            </span>
            <p class="c-table__empty-title">
              No payments found
            </p>
            <p>Try a wider date range or fewer filters.</p>
          </div>
        </div>

        <div
          v-if="shouldShowPagination"
          class="c-payments__footer"
        >
          <span class="c-payments__total">
            {{ paymentPage.totalItems }} payments
          </span>
          <paginate
            v-model="currentPage"
            :page-count="paymentPage.totalPages"
            :page-range="5"
            :margin-pages="1"
            :prev-text="'‹'"
            :next-text="'›'"
            :click-handler="getDataPerPage"
            :container-class="'c-pagination'"
            :prev-link-class="'md-button prev md-elevation-1'"
            :next-link-class="'md-button next md-elevation-1'"
            :page-link-class="'md-button md-elevation-1'"
            :break-view-link-class="'md-button'"
            :active-class="'active'"
            :disabled-class="'disabled'"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import AppIcon from '../../shared/app-icon/AppIcon';
import moment from 'moment';
import isEmpty from "lodash/isEmpty";
import {onlinePermissionsMixin} from "@/mixins/onlinePermissionsMixin";
import {PERMISSION_NAMES} from "@/const/online-permissions";

export default {
  components: {
    AppIcon,
  },

  directives: {
    checkDigit: {
      bind(el) {
        el.addEventListener('keydown', (event) => {
          const numberKeysToIgnore = ['+', '-', 'e'];
          if (numberKeysToIgnore.includes(event.key)) {
            event.preventDefault();
          }
        });
      }
    }
  },

  mixins: [onlinePermissionsMixin],

  data() {
    return {
      permissionName: `${PERMISSION_NAMES.TERMINAL_PAYMENTS.key}.${PERMISSION_NAMES.TERMINAL_PAYMENTS.key}`,
      paymentStatuses: [{name: "Any status", key: 'any'}, {name: 'Successful', key: 'SUCCESSFUL'}, {name: 'Failed', key: 'FAILED'}],
      currentPage: 1,
      limitPerPage: 10,
      today: moment(new Date()).format('YYYY-MM-DD'),
      filtersAreVisible: false,
      filters: {
        status: 'any',
        oldSystemTransactionId: undefined,
        newSystemTransactionId: undefined,
        minAmount: undefined,
        maxAmount: undefined,
        currency: 'any',
        receiverId: undefined,
        senderId: undefined,
        terminalId: undefined,
        fromTime: undefined,
        toTime: undefined
      },
      defaultValues: {
        currentPage: 1,
        limitPerPage: 10,
      },
      defaultFilters: {
        status: 'any',
        oldSystemTransactionId: undefined,
        newSystemTransactionId: undefined,
        minAmount: undefined,
        maxAmount: undefined,
        currency: 'any',
        receiverId: undefined,
        senderId: undefined,
        terminalId: undefined,
        fromTime: undefined,
        toTime: undefined
      },
      formattedFilters: [],
      appliedDates: { fromTime: undefined, toTime: undefined },
      datePresets: [
        { label: 'Today', days: 0 },
        { label: '7 days', days: 6 },
        { label: '30 days', days: 29 },
      ],
    };
  },

  computed: {
    ...mapGetters('terminal', [
      'paymentPage',
      'getPaymentsForTable',
      'isFetchingPaymentsPage',
      'getCurrencies',
      'isFetchingCurrencies'
    ]),

    payments() {
      return (this.paymentPage && this.paymentPage.items) || [];
    },

    // advanced (panel) filters that differ from their defaults
    pendingAdvancedCount() {
      const basicKeys = ['status', 'fromTime', 'toTime'];

      return Object.keys(this.defaultFilters)
        .filter(key => !basicKeys.includes(key) && this.filters[key] !== this.defaultFilters[key])
        .length;
    },

    appliedDateLabel() {
      const { fromTime, toTime } = this.appliedDates;

      if (!fromTime && !toTime) {
        return '';
      }

      const format = date => moment(date).format('DD MMM YYYY');

      if (fromTime && toTime) {
        return fromTime === toTime ? format(fromTime) : `${format(fromTime)} – ${format(toTime)}`;
      }

      return fromTime ? `From ${format(fromTime)}` : `Until ${format(toTime)}`;
    },

    currencies() {
      return [{key: 'any', name: 'Any currency'}, ...Array.from(this.getCurrencies)];
    },

    shouldShowPagination() {
      return this.paymentPage && this.paymentPage.totalItems > this.limitPerPage;
    },

    isClearFiltersDisabled() {
      return JSON.stringify(this.defaultFilters) === JSON.stringify(this.filters);
    },

    maxFromDate() {
      const toTime = new Date(moment(this.filters.toTime)).getTime();
      const today = new Date(moment(this.today)).getTime();
      return (toTime && today > toTime) ? moment(toTime).format('YYYY-MM-DD') : this.today;
    },

    minToDate() {
      const fromTime = new Date(moment(this.filters.fromTime)).getTime();
      const today = new Date(moment(this.today)).getTime();
      return (fromTime && today > fromTime) ? moment(fromTime).format('YYYY-MM-DD') : '';
    },
  },

  watch: {
    'filters.status'(value) {
      if (isEmpty(value)) {
        this.filters.status = 'any';
      }
    },

    'filters.oldSystemTransactionId'(value) {
      if (isEmpty(value)) {
        this.filters.oldSystemTransactionId = undefined;
      }
    },

    'filters.newSystemTransactionId'(value) {
      if (isEmpty(value)) {
        this.filters.newSystemTransactionId = undefined;
      }
    },

    'filters.minAmount'(value) {
      if (isEmpty(value)) {
        this.filters.minAmount = undefined;
      }
    },

    'filters.maxAmount'(value) {
      if (isEmpty(value)) {
        this.filters.maxAmount = undefined;
      }
    },

    'filters.currency'(value) {
      if (isEmpty(value)) {
        this.filters.currency = 'any';
      }
    },

    'filters.receiverId'(value) {
      if (isEmpty(value)) {
        this.filters.receiverId = undefined;
      }
    },

    'filters.senderId'(value) {
      if (isEmpty(value)) {
        this.filters.senderId = undefined;
      }
    },

    'filters.terminalId'(value) {
      if (isEmpty(value)) {
        this.filters.terminalId = undefined;
      }
    },
  },

  async created() {
    this.$material.locale.dateFormat = 'dd.MM.yyyy.';
    this.getPaymentCurrencies();
    this.getDataPerPage();
  },

  methods: {
    ...mapActions('terminal', ['getPaymentPage', 'getPaymentCurrencies']),

    applyDatePreset(preset) {
      this.filters.fromTime = moment(this.today).subtract(preset.days, 'days').format('YYYY-MM-DD');
      this.filters.toTime = this.today;
      this.getDataPerPage(1);
    },

    isPresetActive(preset) {
      return this.filters.toTime === this.today
        && this.filters.fromTime === moment(this.today).subtract(preset.days, 'days').format('YYYY-MM-DD');
    },

    clearDateFilter() {
      this.filters.fromTime = undefined;
      this.filters.toTime = undefined;
      this.getDataPerPage(1);
    },

    formatTime(timeStamp) {
      return moment(timeStamp).format('HH:mm');
    },

    formatDate(timeStamp) {
      return moment(timeStamp).format('DD MMM YYYY');
    },

    formatStatus(status) {
      const paymentStatus = this.paymentStatuses.find(({ key }) => key === status);

      return paymentStatus ? paymentStatus.name : status;
    },

    formatAmount(amount) {
      // same format as before the redesign (store getter): fixed 2 decimals, no thousands separator
      return Number(amount || 0).toFixed(2);
    },

    toggleFilters() {
      this.filtersAreVisible = !this.filtersAreVisible;
    },

    goToHome() {
      this.$router.push({ name: 'home' });
    },

    async fetchPayments(page, pageSize) {
      this.filtersAreVisible = false;
      this.formatFilters();
      this.appliedDates = { fromTime: this.filters.fromTime, toTime: this.filters.toTime };
      await this.getPaymentPage({ page, pageSize, ...this.getFilters() });
    },

    getDataPerPage(
      currentPage = 1,
      limit = this.defaultValues.limitPerPage,
    ) {
      this.currentPage = currentPage;
      this.fetchPayments(currentPage, limit);
    },

    getFilters() {
      return {
        page: this.currentPage,
        pageSize: this.limitPerPage,
        ...this.filters,
        status: this.filters.status === 'any' ? undefined : this.filters.status,
        currency: this.filters.currency === 'any' ? undefined : this.filters.currency,
        fromTime: this.filters.fromTime ? new Date(this.filters.fromTime).setHours(0, 0, 0) : undefined,
        toTime: this.filters.toTime ? new Date(this.filters.toTime).setHours(23, 59, 59) : undefined,
      }
    },

    clearFilters() {
      this.filters = {...this.defaultFilters};
      this.currentPage = this.defaultValues.currentPage;
      this.getDataPerPage(this.currentPage, this.defaultValues.limitPerPage);
    },

    clearFilter(filter) {
      this.filters[filter] = this.defaultFilters[filter];
      this.currentPage = this.defaultValues.currentPage;
      this.getDataPerPage(this.currentPage, this.defaultValues.limitPerPage);
    },

    formatFilters() {
      this.formattedFilters = [];
      const filterNames = {
        'status': 'Status',
        'currency': 'Currency',
        'oldSystemTransactionId': 'Old system ID',
        'newSystemTransactionId': 'New system ID',
        'minAmount': 'Min amount',
        'maxAmount': 'Max amount',
        'receiverId': 'Receiver ID',
        'senderId': 'Sender ID',
        'terminalId': 'Terminal ID'
      };
      for (let filter in this.filters) {
        if (!this.filters[filter] || this.filters[filter] === 'any') {
          continue;
        }
        const formattedFilter = {
          key: filter
        };
        switch (filter) {
          case 'status':
          case 'currency':
          case 'oldSystemTransactionId':
          case 'newSystemTransactionId':
          case 'minAmount':
          case 'maxAmount':
          case 'receiverId':
          case 'senderId':
          case 'terminalId':
            formattedFilter.name = filterNames[filter];
            break;
          default:
            continue;
        }
        formattedFilter.value = this.filters[filter];
        this.formattedFilters.push(formattedFilter);
      }
    }
  },
};
</script>

<style lang="scss" scoped>
.c-payments__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 240px;
}

// ---------- filter bar ----------
.c-payments__filterbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 0 24px 16px;
}

.c-date-range {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 4px 0 12px;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  background-color: var(--bg-surface);
  box-shadow: var(--shadow-xs);
  transition: border-color var(--duration-fast) ease, box-shadow var(--duration-fast) ease;

  &:focus-within {
    border-color: var(--brand-600);
    box-shadow: var(--focus-ring);
  }
}

.c-date-range__icon {
  color: var(--text-muted);
}

.c-date-range__arrow {
  color: var(--text-muted);
}

.c-date-range__field {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  cursor: pointer;

  input {
    height: 32px;
    padding: 0 4px;
    border: none;
    border-radius: var(--radius-sm);
    background: transparent;
    color: var(--text-primary);
    font-family: var(--font-sans);
    font-size: 14px;
    font-variant-numeric: tabular-nums;
    cursor: pointer;

    &:hover {
      background-color: var(--bg-hover);
    }

    &::-webkit-calendar-picker-indicator {
      opacity: 0.55;
      cursor: pointer;
    }
  }
}

.c-date-range__label {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

// segmented control
.c-payments__presets {
  display: inline-flex;
  gap: 2px;
  padding: 3px;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background-color: var(--bg-subtle);
}

.c-payments__preset {
  height: 32px;
  padding: 0 12px;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--text-secondary);
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    color: var(--text-primary);
  }

  &.is-active {
    background-color: var(--bg-surface);
    box-shadow: var(--shadow-sm);
    color: var(--text-primary);
    font-weight: 600;
  }

  &:focus-visible {
    box-shadow: var(--focus-ring);
  }
}

.c-payments__status {
  width: 150px;
}

.c-payments__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;

  .is-open {
    border-color: var(--brand-600);
    background-color: var(--brand-50);
    color: var(--brand-fg);
  }
}

.c-payments__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: var(--radius-full);
  background-color: var(--brand-600);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
}

// ---------- more filters panel ----------
.c-payments__panel {
  margin: 0 24px 16px;
  padding: 20px;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  background-color: var(--bg-subtle);
}

.c-payments__grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.c-payments__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0;

  > span {
    font-size: 13px;
    font-weight: 500;
    color: var(--text-primary);
  }

  .ui-field__input {
    height: 40px;
    font-size: 14px;
  }
}

.c-payments__panel-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
}

.c-payments-panel-enter-active,
.c-payments-panel-leave-active {
  transition: opacity var(--duration-base) ease, transform var(--duration-base) var(--ease-out);
}

.c-payments-panel-enter,
.c-payments-panel-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

// ---------- applied filters ----------
.c-payments__applied {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 24px 16px;
}

.c-payments__applied-label {
  font-size: 13px;
  color: var(--text-muted);
}

.c-payments__tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 0 4px 0 10px;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-full);
  background-color: var(--bg-subtle);
  color: var(--text-secondary);
  font-size: 13px;

  strong {
    font-weight: 600;
    color: var(--text-primary);
  }

  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: transparent;
    color: var(--text-muted);
    cursor: pointer;

    &:hover {
      background-color: var(--bg-hover);
      color: var(--text-primary);
    }
  }
}

.c-payments__clear-all {
  padding: 4px 6px;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--brand-fg);
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background-color: var(--brand-50);
  }
}

// ---------- results ----------
.c-payments__results {
  position: relative;
  border-top: 1px solid var(--border-default);
}

.c-payments__overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 80px;
  background-color: color-mix(in srgb, var(--bg-surface) 65%, transparent);
}

.c-payments-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-family: var(--font-sans);

  th,
  td {
    padding: 12px 16px;
    text-align: left;
    vertical-align: middle;

    &:first-child {
      padding-left: 24px;
    }

    &:last-child {
      padding-right: 24px;
    }

    &.is-numeric {
      text-align: right;
    }
  }

  th {
    position: sticky;
    top: 0;
    z-index: $zIndexStickyTop;
    height: 44px;
    padding-top: 0;
    padding-bottom: 0;
    background-color: var(--bg-subtle);
    border-bottom: 1px solid var(--border-default);
    color: var(--text-secondary);
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    white-space: nowrap;
  }

  td {
    border-bottom: 1px solid var(--border-default);
    font-size: 14px;
    color: var(--text-primary);
  }

  tbody tr {
    transition: background-color var(--duration-fast) ease;

    &:hover {
      background-color: var(--bg-subtle);
    }
  }
}

.c-payments-table__primary {
  display: block;
  font-weight: 500;
  line-height: 1.35;
  white-space: nowrap;
}

.c-payments-table__secondary {
  display: block;
  margin-top: 2px;
  font-size: 12px;
  color: var(--text-muted);
  white-space: nowrap;
}

.c-payments-table__id {
  display: block;
  margin-top: 2px;
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 12px;
  color: var(--text-muted);
  white-space: nowrap;
}

.c-payments-table__id--main {
  margin-top: 0;
  font-size: 13px;
  color: var(--text-primary);
}

.c-payments-table__status {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 24px;
  padding: 0 10px 0 8px;
  border: 1px solid;
  border-radius: var(--radius-full);
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.c-payments-table__status--successful {
  border-color: var(--success-border);
  background-color: var(--success-bg);
  color: var(--success-fg);
}

.c-payments-table__status--failed {
  border-color: var(--danger-border);
  background-color: var(--danger-bg);
  color: var(--danger-fg);
}

.c-payments-table__amount {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.c-payments-table__currency {
  margin-left: 4px;
  font-size: 12px;
  font-weight: 500;
  color: var(--text-muted);
}

.c-payments__footer {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 24px;
  border-top: 1px solid var(--border-default);
}

.c-payments__total {
  position: absolute;
  left: 24px;
  font-size: 13px;
  color: var(--text-muted);
}
</style>
